import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
// Use Shopify's versioned asset URL for the dependency as well as this entry.
// Relative imports otherwise keep an old CDN/browser copy after a theme release.
const { METRES_PER_FOOT, positiveNumber, calculateArea, recommend, optionIndex, resolveVariant, canOrder, availableRecommendations, unitPrice, formatMoney } =
  await import(document.querySelector('script[data-calculator-core-url]').dataset.calculatorCoreUrl);

class PaintCalculator extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    try { this.config = JSON.parse(this.querySelector('[data-calculator-config]').textContent); }
    catch { return; }
    this.controller = new AbortController();
    this.dialog = this.querySelector('dialog');
    this.scope = this.closest('.shopify-section');
    this.colorIndex = optionIndex(this.config.options, this.config.colorOption, 'color');
    this.sizeIndex = optionIndex(this.config.options, this.config.sizeOption, 'size');
    this.unit = 'ft';
    this.rows = [{ width: null, height: null, count: 1 }];
    this.projectQuantity = 1;
    this.scenario = this.querySelector('[data-scene]')?.dataset.scene;
    this.step = 1;
    this.busy = false;
    const { signal } = this.controller;
    this.addEventListener('click', e => this.onClick(e), { signal });
    this.addEventListener('input', e => this.onInput(e), { signal });
    this.addEventListener('change', e => { if (e.target.matches('[data-units]')) this.changeUnit(e.target.value); }, { signal });
    this.dialog.addEventListener('cancel', e => { e.preventDefault(); if (!this.busy) this.close(); }, { signal });
    this.dialog.addEventListener('close', () => this.afterClose(), { signal });
    this.dialog.addEventListener('keydown', e => this.trapTab(e), { signal });
    this.q('[data-kit-image]').addEventListener('error', () => { this.q('[data-kit-media]').hidden = true; }, { signal });
    this.dialog.addEventListener('pointerdown', e => { this.backdropDown = e.target === this.dialog && e.clientX < this.dialog.getBoundingClientRect().left; }, { signal });
    this.scope.addEventListener(StandardEvents.productSelect, e => {
      if (e.target.closest('dwil-paint-calculator')) return;
      e.promise?.then(() => {
        if (!this.isConnected || !this.dialog.open) return;
        this.selected = this.readSelected();
        this.color = this.selected[this.colorIndex];
        if (this.step === 2) this.renderRecommendation();
      }).catch(() => {});
    }, { signal });
    document.addEventListener('shopify:block:select', e => {
      if (e.target === this || this.contains(e.target)) this.open();
    }, { signal });
    this.q('[data-open]').hidden = false;
    this.renderScene();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    if (this.dialog?.open) { this.dialog.close(); this.afterClose(); }
  }

  q(selector) { return this.querySelector(selector); }
  text(selector, value) { this.q(selector).textContent = value; }
  money(cents) { return formatMoney(cents, this.config.moneyFormat, this.config.currency, this.config.locale); }

  readSelected() {
    const formId = this.scope.querySelector('product-form-component input[name="id"]')?.value;
    const base = this.config.variants.find(v => String(v.id) === formId) || this.config.variants.find(v => v.id === this.config.selectedId);
    return this.config.options.map((option, i) => {
      const controls = [...this.scope.querySelectorAll('variant-picker [data-option-name]')];
      return controls.find(el => el.dataset.optionName === option.name && (el.checked || el.selected))?.value || base?.options[i] || option.values[0]?.name;
    });
  }

  open() {
    if (this.dialog.open || this.busy) return;
    this.selected = this.readSelected();
    this.color = this.selected[this.colorIndex];
    this.q('[data-cart-error]').hidden = true;
    this.q('[data-open]').setAttribute('aria-expanded', 'true');
    this.oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.locked = true;
    this.dialog.showModal();
    this.setStep(1);
  }

  close() {
    this.dialog.close();
    // Restore before opening the native cart; its scroll lock must win.
    this.afterClose();
  }

  afterClose() {
    if (!this.locked) return;
    this.locked = false;
    document.body.style.overflow = this.oldOverflow;
    this.q('[data-open]').setAttribute('aria-expanded', 'false');
    if (this.isConnected) this.q('[data-open]').focus({ preventScroll: true });
  }

  trapTab(event) {
    if (event.key !== 'Tab') return;
    const items = [...this.dialog.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),a[href]')].filter(el => el.getClientRects().length);
    const first = items[0], last = items.at(-1);
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.q('[data-title]'))) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  onClick(event) {
    if (this.busy) return;
    if (event.target === this.dialog && this.backdropDown) { this.close(); return; }
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    if (button.matches('[data-open]')) this.open();
    if (button.matches('[data-close]')) this.close();
    if (button.matches('[data-scene]')) { this.scenario = button.dataset.scene; this.renderScene(); }
    if (button.matches('[data-minus],[data-plus]')) {
      const next = (positiveNumber(this.projectQuantity, 20) || 1) + (button.hasAttribute('data-plus') ? 1 : -1);
      this.projectQuantity = Math.min(20, Math.max(1, next));
      this.q('[data-quantity]').value = this.projectQuantity;
      this.renderArea();
    }
    if (button.matches('[data-add-row]') && this.rows.length < 20) {
      this.rows.push({ width: null, height: null, count: 1 }); this.renderRows(); this.renderArea();
      this.q('[data-rows]').lastElementChild.querySelector('input').focus();
    }
    if (button.matches('[data-remove-row]')) {
      this.rows.splice(Number(button.dataset.removeRow), 1); this.renderRows(); this.renderArea(); this.q('[data-add-row]').focus();
    }
    if (button.matches('[data-continue]') && this.area) this.setStep(2);
    if (button.matches('[data-back],[data-top-back]')) this.setStep(1);
    if (button.matches('[data-color]')) {
      this.color = button.dataset.color;
      this.alternativeValue = null;
      this.selected[this.colorIndex] = this.color;
      this.renderRecommendation();
      if (canOrder(this.variant, this.recommendation?.quantity)) this.syncPdp();
    }
    if (button.matches('[data-alternative]')) {
      this.alternativeValue = button.dataset.alternative;
      this.renderRecommendation();
      this.syncPdp();
    }
    if (button.matches('[data-add]')) void this.addToCart();
  }

  onInput(event) {
    const input = event.target;
    if (input.matches('[data-quantity]')) this.projectQuantity = input.value;
    if (input.matches('[data-field]')) {
      const { field, row } = input.dataset;
      const value = positiveNumber(input.value, field === 'count' ? 20 : 100000);
      this.rows[Number(row)][field] = value == null ? null : field === 'count' ? value : value / (this.unit === 'm' ? METRES_PER_FOOT : 1);
      input.setAttribute('aria-invalid', String(value == null || (field === 'count' && !Number.isInteger(value))));
    }
    this.renderArea();
  }

  changeUnit(unit) {
    this.unit = unit;
    // Rows are always stored in feet; unit toggles never re-round the source data.
    this.renderRows(); this.renderArea();
  }

  renderRows() {
    const host = this.q('[data-rows]');
    host.replaceChildren();
    const wall = this.scenario === 'walls';
    this.rows.forEach((row, index) => {
      const group = document.createElement('div'); group.className = 'dpc-row';
      const head = document.createElement('div'); head.className = 'dpc-row-head';
      const title = document.createElement('span'); title.textContent = `${this.config.furniture ? 'Surface' : wall ? 'Wall' : 'Area'} ${index + 1}`; head.append(title);
      if (index) {
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'dpc-remove'; remove.dataset.removeRow = index;
        remove.textContent = 'Remove'; remove.setAttribute('aria-label', `Remove ${wall ? 'wall' : 'area'} ${index + 1}`); head.append(remove);
      }
      group.append(head);
      const fields = document.createElement('div'); fields.className = 'dpc-fields';
      for (const field of ['width','height','count']) {
        const label = document.createElement('label');
        const caption = document.createElement('span'); caption.className = 'dpc-label';
        caption.textContent = field === 'count' ? `Number of ${this.config.furniture ? 'surfaces' : wall ? 'walls' : 'areas'} with these dimensions` : `${field === 'height' ? (this.config.furniture ? 'Length / height' : 'Height') : this.scenario === 'backsplash' ? 'Total length' : 'Width'} (${this.unit})`;
        const input = document.createElement('input'); input.type = 'text'; input.inputMode = field === 'count' ? 'numeric' : 'decimal';
        input.dataset.field = field; input.dataset.row = index; input.autocomplete = 'off';
        input.id = `${this.id}-row-${index}-${field}`;
        input.setAttribute('aria-describedby', `${this.id}-input-error`);
        const value = row[field];
        input.value = value == null ? '' : field === 'count' ? String(value) : String(Number((value * (this.unit === 'm' ? METRES_PER_FOOT : 1)).toFixed(6)));
        label.append(caption,input); fields.append(label);
      }
      group.append(fields); host.append(group);
    });
    this.q('[data-add-row]').disabled = this.rows.length >= 20;
    this.text('[data-add-row]', this.config.furniture ? 'Add another surface' : this.scenario === 'walls' ? 'Add another wall' : 'Add another area');
  }

  renderScene() {
    for (const button of this.querySelectorAll('[data-scene]')) button.setAttribute('aria-pressed', String(button.dataset.scene === this.scenario));
    const preset = this.config.presets[this.scenario];
    this.q('[data-fixed]').hidden = preset == null;
    this.q('[data-measures]').hidden = preset != null;
    this.text('[data-preset-note]', `Approx. ${preset} sq ft per project. Allowance already included.`);
    if (preset == null) this.renderRows();
    this.renderArea();
  }

  renderArea() {
    this.area = this.scenario ? calculateArea({ preset: this.config.presets[this.scenario], quantity: this.projectQuantity, rows: this.rows, waste: this.config.waste }) : null;
    this.q('[data-continue]').disabled = !this.area;
    this.text('[data-area]', this.area ? `${Math.round(this.area)} sq ft` : '—');
    this.q('[data-minus]').disabled = Number(this.projectQuantity) <= 1;
    this.q('[data-plus]').disabled = Number(this.projectQuantity) >= 20;
    this.q('[data-input-error]').hidden = !!this.area || this.config.presets[this.scenario] == null;
    this.text('[data-input-error]', this.config.presets[this.scenario] != null ? 'Enter a whole number of projects from 1 to 20.' : '');
  }

  setStep(step) {
    if (step === 1) this.alternativeValue = null;
    this.step = step;
    this.q('[data-step-one]').hidden = step !== 1;
    this.q('[data-step-two]').hidden = step !== 2;
    this.q('[data-footer-one]').hidden = step !== 1;
    this.q('[data-footer-two]').hidden = step !== 2;
    this.q('[data-top-back]').hidden = step !== 2;
    this.text('[data-step-label]', `Step ${step} of 2`);
    this.text('[data-title]', step === 1 ? 'What are you painting?' : 'Choose your color');
    if (step === 2) this.renderRecommendation();
    this.q('.dpc-body').scrollTop = 0;
    this.q('[data-title]').focus({ preventScroll: true });
  }

  variantFor(color) {
    return resolveVariant({ variants:this.config.variants, colorIndex:this.colorIndex, sizeIndex:this.sizeIndex, color, value:this.recommendation?.value, selected:this.selected, fixedKit:this.config.fixedKit === true });
  }

  alternativesFor(color) {
    return availableRecommendations({ area: this.area, tiers: this.config.tiers, variants: this.config.variants,
      colorIndex: this.colorIndex, sizeIndex: this.sizeIndex, color, selected: this.selected });
  }

  renderRecommendation() {
    const focusedColor = this.contains(document.activeElement) ? document.activeElement.dataset.color : null;
    const focusedAlternative = this.contains(document.activeElement) ? document.activeElement.dataset.alternative : null;
    const base = recommend(this.area, this.config.tiers);
    const alternatives = this.config.furniture ? this.alternativesFor(this.color) : [];
    this.recommendation = alternatives.find(t => t.value === this.alternativeValue) || base;
    const r = this.recommendation;
    if (!r) { this.q('[data-add]').disabled = true; return; }
    this.text('[data-kit]', r.label || `${r.coverage} sq ft Kit`);
    this.text('[data-kit-count]', r.quantity === 1 ? '1 kit is enough' : `${r.quantity} kits are recommended`);
    this.text('[data-kit-coverage]', this.config.fixedKit ? `Planned coverage: ${r.totalCoverage} sq ft (40 sq ft per kit)` : `Covers up to ${r.totalCoverage} sq ft`);
    this.text('[data-project-area]', `Your project: ${Math.round(this.area)} sq ft`);
    this.text('[data-order-quantity]', r.quantity);
    this.text('[data-selected-color]', this.color || 'Standard');
    const host = this.q('[data-colors]'); host.replaceChildren();
    for (const option of this.config.options[this.colorIndex]?.values || []) {
      const button = document.createElement('button'); button.type = 'button'; button.className = 'dpc-color'; button.dataset.color = option.name;
      button.setAttribute('aria-pressed', String(option.name === this.color));
      const v = this.variantFor(option.name);
      const currentSizeAvailable = canOrder(v,r.quantity);
      button.disabled = this.config.furniture ? this.alternativesFor(option.name).length === 0 : !currentSizeAvailable;
      button.setAttribute('aria-label', `${option.name}${button.disabled ? ' — unavailable for this project' : !currentSizeAvailable && this.config.furniture ? ' — choose an alternative size' : ''}`);
      const swatch = document.createElement('span'); swatch.className = 'dpc-swatch'; swatch.setAttribute('aria-hidden','true');
      const native = [...this.scope.querySelectorAll('[data-dwil-color-option]')].find(b => b.dataset.optionValueId === String(option.id))?.querySelector('.dwil-pdp-native-picker__swatch');
      if (native) {
        const style = getComputedStyle(native);
        swatch.style.setProperty('--swatch-color', style.getPropertyValue('--dwil-swatch-color') || style.backgroundColor);
        swatch.style.setProperty('--swatch-image', style.getPropertyValue('--dwil-swatch-image') || 'none');
      } else if (option.image) swatch.style.setProperty('--swatch-image', `url("${option.image}")`);
      else if (option.swatch) swatch.style.setProperty('--swatch-color', option.swatch);
      const name = document.createElement('span'); name.textContent = option.name; button.append(swatch,name); host.append(button);
    }
    this.variant = this.variantFor(this.color);
    const image = this.q('[data-kit-image]');
    const imageUrl = this.variant?.image;
    this.q('[data-kit-media]').hidden = !imageUrl;
    if (imageUrl) {
      image.alt = this.config.fixedKit ? `${this.color} — complete kit with cleaner, paint and gloss topcoat` : `${this.color || 'Paint'} — ${this.recommendation.coverage} sq ft kit`;
      if (image.getAttribute('src') !== imageUrl) image.src = imageUrl;
    } else image.removeAttribute('src');
    const valid = canOrder(this.variant,r.quantity);
    const alternativeHost = this.q('[data-alternatives]');
    if (alternativeHost) {
      const baseVariant = resolveVariant({ variants: this.config.variants, colorIndex: this.colorIndex, sizeIndex: this.sizeIndex,
        color: this.color, value: base?.value, selected: this.selected });
      const needsAlternative = this.config.furniture && !canOrder(baseVariant, base?.quantity);
      alternativeHost.hidden = !needsAlternative || !alternatives.length;
      const list = this.q('[data-alternative-list]'); list.replaceChildren();
      this.text('[data-alternative-title]', `Available sizes in ${this.color}`);
      if (needsAlternative) for (const choice of alternatives) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'dpc-color';
        button.dataset.alternative = choice.value;
        button.setAttribute('aria-pressed', String(choice.value === this.alternativeValue));
        button.textContent = `${choice.quantity} × ${choice.label || choice.value} — covers ${choice.totalCoverage} sq ft`;
        list.append(button);
      }
    }
    this.q('[data-add]').disabled = !valid || this.busy;
    this.q('[data-variant-error]').hidden = valid;
    const error = this.config.furniture
      ? (alternatives.length ? `The recommended size or quantity is unavailable in ${this.color}. Choose an available size below; your color stays the same.` : `No available size in ${this.color} can cover this project right now. Try another color or contact us.`)
      : this.variant?.available ? 'The recommended quantity is not currently available for this color. Please choose another color or contact us.' : this.config.fixedKit ? 'This complete kit is currently unavailable in this color. Please choose another color.' : 'This color is currently unavailable in the recommended size. Please choose another color.';
    this.text('[data-variant-error]', error);
    this.renderPrices();
    this.q('[data-cart-error]').hidden = true;
    if (focusedColor) [...host.children].find(button => button.dataset.color === focusedColor && !button.disabled)?.focus({preventScroll:true});
    if (focusedAlternative) [...this.querySelectorAll('[data-alternative]')].find(button => button.dataset.alternative === focusedAlternative)?.focus({preventScroll:true});
  }

  renderPrices() {
    const quantity = this.recommendation?.quantity;
    const valid = canOrder(this.variant, quantity);
    const price = this.variant ? unitPrice(this.variant, this.config.promotion) : null;
    const discounted = price != null && price < this.variant.price;
    for (const [current, original, available, count] of [
      ['[data-unit-price]', '[data-unit-original]', price != null, 1],
      ['[data-total]', '[data-total-original]', valid, quantity],
      ['[data-add-price]', '[data-add-original]', valid, quantity]
    ]) {
      this.text(current, available ? this.money(price * count) : '—');
      this.text(original, available && discounted ? this.money(this.variant.price * count) : '');
      this.q(original).setAttribute('aria-label', available && discounted ? `Original price ${this.money(this.variant.price * count)}` : 'Original price');
      this.q(original).hidden = !available || !discounted;
    }
    this.text('[data-add-label]', 'Add to cart');
    this.q('[data-add-prices]').hidden = !valid;
    this.q('[data-discount-note]').hidden = !valid || !discounted;
  }

  syncPdp() {
    const v = this.variantFor(this.color);
    if (!v) return;
    const picker = this.scope.querySelector('[data-dwil-native-picker] variant-picker') || this.scope.querySelector('variant-picker');
    if (!picker?.updateSelectedOption) return;
    const controls = [...picker.querySelectorAll('[data-option-name]')];
    const changed = [];
    this.config.options.forEach((option,index) => {
      const el = controls.find(c => c.dataset.optionName === option.name && c.value === v.options[index]);
      if (!el) return;
      if (el instanceof HTMLOptionElement ? !el.selected : !el.checked) changed.push(el);
    });
    changed.forEach((el,index) => {
      const target = el instanceof HTMLOptionElement ? el.parentElement : el;
      if (el instanceof HTMLOptionElement) target.value = el.value;
      // The native change handler updates its final option itself. Updating it
      // twice corrupts Horizon's current/previous selection bookkeeping.
      if (index === changed.length - 1) target.dispatchEvent(new Event('change',{bubbles:true}));
      else picker.updateSelectedOption(target);
    });
  }

  async addToCart() {
    if (this.busy || !canOrder(this.variant,this.recommendation?.quantity)) return;
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (!drawer) { this.cartError('The cart is not ready. Please close the calculator and try again.'); return; }
    this.busy = true;
    this.q('[data-add]').disabled = true;
    this.q('[data-close]').disabled = true;
    this.q('[data-back]').disabled = true;
    this.q('[data-top-back]').disabled = true;
    this.dialog.setAttribute('aria-busy','true');
    this.text('[data-add-label]','Adding…');
    this.q('[data-add-prices]').hidden = true;
    const variant = this.variant, quantity = this.recommendation.quantity;
    let added = false;
    let deferred;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el=>el.dataset.sectionId))].slice(0,5);
      const response = await fetch(this.config.cartAddUrl,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity}],sections,sections_url:location.pathname})});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'We could not add this kit. Please try again.');
      added = true;
      deferred = CartLinesUpdateEvent.createPromise();
      const early = CartLinesUpdateEvent.createPromise();
      const cartEvent = Object.assign(new CartLinesUpdateEvent({ action:'add',context:'product',lines:[{merchandiseId:String(variant.id),quantity}],promise:deferred.promise }),{dwilSectionsReady:early.promise});
      this.text('[data-add-label]','Added');
      this.close();
      // Dispatch from the block, outside the now-closed modal. The native cart owns rendering/opening.
      this.dispatchEvent(cartEvent);
      early.resolve({detail:{sections:result.sections,didError:false}});
      const cartResponse = await fetch(this.config.cartUrl,{headers:{Accept:'application/json'},cache:'no-store'});
      if (!cartResponse.ok) { deferred.resolve({detail:{sections:result.sections,didError:false}}); }
      else {
        const cart = await cartResponse.json();
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,didError:false,source:'dwil-paint-calculator',itemCount:quantity,productId:String(this.config.productId)}});
      }
      requestAnimationFrame(()=>{ if (!drawer.isOpen) drawer.open(); });
    } catch (error) {
      if (added) {
        // An add succeeded: never offer a blind retry that could duplicate cart lines.
        deferred?.resolve({detail:{didError:false}});
        drawer.open();
      } else this.cartError(error instanceof TypeError ? 'Connection interrupted. Check your cart before trying again.' : error.message || 'We could not add this kit. Please try again.');
    } finally {
      this.busy = false;
      this.q('[data-close]').disabled = false;
      this.q('[data-back]').disabled = false;
      this.q('[data-top-back]').disabled = false;
      this.dialog.removeAttribute('aria-busy');
      this.q('[data-add]').disabled = !canOrder(this.variant,this.recommendation?.quantity);
      if (this.dialog.open) this.renderPrices();
    }
  }

  cartError(message) {
    this.text('[data-cart-error]', message);
    this.q('[data-cart-error]').hidden = false;
  }
}

if (!customElements.get('dwil-paint-calculator')) customElements.define('dwil-paint-calculator',PaintCalculator);
